/**
 * AdminAuthModal — Reusable Passwort-Bestätigung für sensible UI-Aktionen.
 *
 * Verwendungszweck (Phase C): POS Lock Mode aktivieren / verlassen.
 * Ruft ``POST /api/auth/verify-admin`` mit dem eingegebenen Passwort auf.
 * Speichert kein Passwort im Frontend, hält keine langlebigen Tokens.
 * Server prüft: Session ist Admin UND Passwort stimmt. 401/403 zeigt eine
 * verständliche Fehlermeldung, ohne die eigentliche Login-Session zu
 * verändern.
 */
import React, { useState, useEffect, useRef } from "react";
import { Lock, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, formatApiErrorDetail } from "../lib/api";

export default function AdminAuthModal({
  open,
  title,
  description,
  confirmLabel = "Bestätigen",
  onConfirmed,
  onClose,
}) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPassword("");
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  if (!open) return null;

  async function submit(e) {
    e?.preventDefault?.();
    if (!password) return;
    setBusy(true);
    try {
      await api.post("/auth/verify-admin", { password });
      onConfirmed?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      toast.error(
        status === 403
          ? "Nur Administratoren können diese Aktion ausführen."
          : formatApiErrorDetail(detail) || "Passwort ist falsch."
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/80 flex items-center justify-center p-4"
      data-testid="admin-auth-modal"
    >
      <form
        onSubmit={submit}
        className="bg-zinc-950 border border-zinc-800 rounded-lg w-full max-w-md p-6 space-y-4"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-cyan-500/15 text-cyan-400 flex items-center justify-center shrink-0">
            <Lock size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg font-bold uppercase tracking-wide">
              {title}
            </div>
            {description && (
              <div className="text-sm text-zinc-400 mt-1 leading-relaxed">
                {description}
              </div>
            )}
          </div>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={onClose}
            className="text-zinc-500"
            data-testid="admin-auth-close-btn"
          >
            <X size={16} />
          </Button>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-400">
            Admin-Passwort
          </Label>
          <Input
            ref={inputRef}
            type="password"
            data-testid="admin-auth-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm"
            required
          />
        </div>

        <div className="flex gap-2 pt-1">
          <Button
            type="button"
            variant="ghost"
            className="flex-1"
            onClick={onClose}
            disabled={busy}
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            data-testid="admin-auth-confirm-btn"
            className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-black font-bold"
            disabled={busy || !password}
          >
            {busy ? "Prüfe…" : confirmLabel}
          </Button>
        </div>
      </form>
    </div>
  );
}
